/**
 * The single configuration point for the Kudu applet origin. Everything that
 * constructs an applet iframe `src`, targets a `postMessage`, or validates an
 * inbound `event.origin` reads the origin from here, so switching from the
 * interim Firebase `web.app` domain to `applets.kudu.com` is a one-line env
 * change with no code edits.
 */

/** Configured applet origin (e.g. `https://applets.kudu.com`), or null if unset. */
export function getAppletOrigin(): string | null {
  const raw = import.meta.env.VITE_APPLET_ORIGIN;
  if (!raw) {
    return null;
  }
  try {
    return new URL(raw).origin;
  } catch {
    return null;
  }
}

/** True when `origin` is exactly the configured applet origin. */
export function isAppletOrigin(origin: string): boolean {
  const configured = getAppletOrigin();
  return configured !== null && origin === configured;
}

/** True when `rawUrl`'s origin is the configured applet origin. */
export function isOnAppletOrigin(rawUrl: string): boolean {
  const origin = getAppletOrigin();
  if (!origin) {
    return false;
  }
  try {
    return new URL(rawUrl).origin === origin;
  } catch {
    return false;
  }
}

/**
 * Build the iframe `src` for a path-based applet: any URL on the applet origin,
 * with its full path + query preserved and the parent origin appended. Returns
 * null if the URL is not on the applet origin.
 */
export function buildRawAppletSrc(rawUrl: string): string | null {
  const origin = getAppletOrigin();
  if (origin === null || typeof window === 'undefined') {
    return null;
  }
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }
  if (url.origin !== origin) {
    return null;
  }
  url.searchParams.set('parentOrigin', window.location.origin);
  return url.toString();
}

export interface JupyterliteEmbed {
  appletId: 'jupyterlite';
  /** Full applet-origin URL to the content-site notebook page. */
  srcOverride: string;
  /** JupyterLite needs storage + service worker → allow-same-origin. */
  sandbox: string;
  /** IDE-appropriate fixed height in px. */
  fixedHeight: number;
  /** JupyterLite notebooks can be reset to the deployed original. */
  resettable: true;
  /** Label for the host's reset control. */
  resetLabel: string;
}

const JUPYTERLITE_PATH_RE = /^\/jupyterlite\/[a-z0-9][a-z0-9-]*\//i;

/**
 * Recognize a stored iframe source as a JupyterLite content-site embed: on the
 * applet origin, under `/jupyterlite/<assignment>/`. These are path-based
 * applets (not `/<id>/<version>/`), rendered in a storage-capable sandbox at a
 * tall fixed height. Returns null otherwise.
 */
export function parseJupyterliteUrl(rawUrl: string): JupyterliteEmbed | null {
  const origin = getAppletOrigin();
  if (!origin) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }
  if (url.origin !== origin || !JUPYTERLITE_PATH_RE.test(url.pathname)) {
    return null;
  }
  return {
    appletId: 'jupyterlite',
    srcOverride: rawUrl,
    sandbox: 'allow-scripts allow-same-origin allow-downloads',
    fixedHeight: 720,
    resettable: true,
    resetLabel: 'Reset notebook',
  };
}

export interface ParsedApplet {
  appletId: string;
  version: string;
  config: Record<string, unknown>;
}

const APPLET_PATH_RE = /^\/([a-z0-9][a-z0-9-]*)\/(v[0-9]+|latest)\/?/i;

/**
 * Recognize a stored iframe source as an applet reference: it must be on the
 * configured applet origin and match the `/<appletId>/<version>/` path shape.
 * Returns the parsed reference (with any `?config=<json>` query decoded) or
 * null if the URL is not an applet (leave it as a plain iframe).
 */
export function parseAppletUrl(rawUrl: string): ParsedApplet | null {
  const origin = getAppletOrigin();
  if (!origin) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }

  if (url.origin !== origin) {
    return null;
  }

  const match = APPLET_PATH_RE.exec(url.pathname);
  if (!match) {
    return null;
  }

  return {
    appletId: match[1],
    version: match[2],
    config: decodeConfig(url.searchParams.get('config')),
  };
}

function decodeConfig(raw: string | null): Record<string, unknown> {
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

/**
 * Build the iframe `src` for an applet: origin + id + version, plus the parent
 * origin so the applet can validate/target messages. Config is delivered over
 * postMessage (`init`), never in the URL.
 */
export function buildAppletSrc(applet: {
  appletId: string;
  version: string;
}): string | null {
  const origin = getAppletOrigin();
  if (origin === null || typeof window === 'undefined') {
    return null;
  }
  const url = new URL(
    `/${applet.appletId}/${applet.version}/`,
    `${origin}/`,
  );
  url.searchParams.set('parentOrigin', window.location.origin);
  return url.toString();
}
